/**
 * InventionSummaryStep — Step 2 of the Claims Workspace.
 *
 * Auto-generates an invention summary from the solution description,
 * problem statement, and product description. The user can review, edit,
 * or regenerate the summary before proceeding to ranking.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

export interface InventionSummaryStepProps {
  subProjectId: string;
  solutionId: string;
  initialSummary: string;
  version: number;
  onSummaryChange: (summary: string) => void;
  onNext: () => void;
  t: (key: string, fallback?: string) => string;
}

const MAX_CHARS = 700;

const InventionSummaryStep: React.FC<InventionSummaryStepProps> = ({
  subProjectId,
  solutionId,
  initialSummary,
  version,
  onSummaryChange,
  onNext,
  t,
}) => {
  const [summary, setSummary] = useState(initialSummary || '');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Auto-generate on mount if no summary exists
  useEffect(() => {
    if (!initialSummary) {
      handleGenerate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await api.post<{ invention_summary: string }>(
        `/sub-projects/${subProjectId}/solutions/${solutionId}/claims/generate-summary`,
        { solution_id: solutionId },
      );
      const text = res.data.invention_summary || '';
      setSummary(text);
      onSummaryChange(text);
      setDirty(false);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('claimsWorkspace.summary.generateError', 'Failed to generate summary.'));
    } finally {
      setGenerating(false);
    }
  }, [subProjectId, solutionId, onSummaryChange, t]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(
        `/sub-projects/${subProjectId}/solutions/${solutionId}?version=${version}`,
        { invention_summary: summary },
      );
      onSummaryChange(summary);
      setDirty(false);
    } catch {
      setError(t('claimsWorkspace.summary.saveError', 'Failed to save summary.'));
    } finally {
      setSaving(false);
    }
  }, [subProjectId, solutionId, summary, onSummaryChange, t]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value.slice(0, MAX_CHARS);
    setSummary(val);
    setDirty(true);
  };

  const canProceed = summary.trim().length > 0;

  return (
    <div className="flex flex-col gap-3 p-4 overflow-auto h-full">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">
          {t('claimsWorkspace.summary.title', 'Invention Summary')}
        </h3>
        <span className="text-[10px] text-slate-400">
          {summary.length}/{MAX_CHARS}
        </span>
      </div>

      <p className="text-xs text-slate-500">
        {t('claimsWorkspace.summary.description', 'This summary describes the core invention. It is used as context for prior art ranking and claim drafting. Review and edit if needed, then proceed.')}
      </p>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      <textarea
        value={summary}
        onChange={handleChange}
        disabled={generating}
        maxLength={MAX_CHARS}
        rows={6}
        className="w-full text-xs border border-slate-300 rounded px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-50 disabled:text-slate-400"
        placeholder={t('claimsWorkspace.summary.placeholder', 'The invention summary will be generated automatically...')}
      />

      <div className="flex items-center gap-2">
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="text-xs px-3 py-1.5 border border-slate-300 rounded text-slate-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {generating
            ? t('common.loading', 'Loading...')
            : t('claimsWorkspace.summary.regenerate', 'Regenerate')}
        </button>

        {dirty && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-xs px-3 py-1.5 border border-green-300 rounded text-green-700 hover:border-green-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? t('common.loading', 'Loading...') : t('claimsWorkspace.summary.save', 'Save')}
          </button>
        )}

        <div className="ml-auto">
          <button
            onClick={onNext}
            disabled={!canProceed}
            className="text-xs px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('claimsWorkspace.summary.next', 'Next: Rank Prior Art')} →
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventionSummaryStep;
